import EventEmitter2 from 'eventemitter2'

export interface SignalingOptions {
  serverUrl: string
}

export enum SignalingRole {
  IDLE = 'IDLE',
  HOST = 'HOST',
  CLIENT = 'CLIENT'
}

export enum SignalingStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CLOSED = 'CLOSED'
}

export class Signaling extends EventEmitter2 {
  public get role() {
    return this.internalRole
  }

  public get status() {
    return this.internalStatus
  }

  public get sessionId() {
    return this.internalSessionId
  }

  public get clientId() {
    return this.internalClientId
  }

  private options: SignalingOptions
  private webSocket: WebSocket | null = null

  private internalRole: SignalingRole = SignalingRole.IDLE
  private internalStatus: SignalingStatus = SignalingStatus.INACTIVE
  private internalSessionId: string = ''
  private internalClientId: string = ''

  public constructor(options: SignalingOptions) {
    super({ wildcard: true })
    this.options = options
  }

  public async createSession() {
    this.internalRole = SignalingRole.HOST
    this.internalStatus = SignalingStatus.ACTIVE

    await this.setupWebSocket((message) => {
      this.handleAsHostMessage(message)
    })

    if (this.webSocket) {
      this.emit('creating-session', {})
      this.webSocket!.send(JSON.stringify({ type: 'create-session' }))
    }
  }

  public async joinSession(sessionId: string, metadata?: Record<string, any>) {
    this.internalRole = SignalingRole.CLIENT
    this.internalStatus = SignalingStatus.ACTIVE

    await this.setupWebSocket((message) => {
      this.handleAsClientMessage(message, metadata)
    })

    if (this.webSocket) {
      this.emit('joining-session', { sessionId, metadata })
      this.webSocket!.send(JSON.stringify({ type: 'join-session', sessionId, metadata }))
    }
  }

  private async setupWebSocket(onMessage: (message: { type: string; [key: string]: any }) => void) {
    return new Promise((resolve) => {
      if (this.webSocket) {
        this.emit('websocket-already-connected', {})
        return
      }

      this.webSocket = new WebSocket(this.options.serverUrl)

      this.webSocket.onopen = () => {
        this.emit('websocket-connected', {})
        resolve(true)
      }

      this.webSocket.onclose = () => {
        this.emit('websocket-closed', {})
        this.webSocket = null
        this.internalStatus = SignalingStatus.CLOSED
      }

      this.webSocket.onerror = (event) => {
        this.emit('websocket-error', { error: event })
        this.webSocket = null
        this.internalStatus = SignalingStatus.CLOSED
        resolve(false)
      }

      this.webSocket.onmessage = (event) => {
        this.handleMessage(onMessage, event)
      }

      this.emit('connecting-websocket', {})
    })
  }

  public sendOffer(offer: any, clientId: string, metadata?: Record<string, any>) {
    if (this.webSocket) {
      this.emit('sending-offer', { offer: offer, clientId: clientId })
      this.webSocket!.send(JSON.stringify({ type: 'send-offer', offer: offer, clientId: clientId, metadata: metadata || {} }))
      this.emit('offer-sent', { offer: offer, clientId: clientId })
    }
  }

  public sendAnswer(answer: any) {
    if (this.webSocket) {
      this.emit('sending-answer', { answer: answer, clientId: this.clientId })
      this.webSocket!.send(JSON.stringify({ type: 'offer-answer', answer, clientId: this.clientId }))
      this.emit('answer-sent', { answer: answer, clientId: this.clientId })
    }
  }

  public sendIceCandidate(candidate: RTCIceCandidateInit, clientId?: string) {
    if (this.webSocket) {
      this.emit('sending-ice-candidate', { candidate: candidate, clientId: clientId || this.clientId })
      this.webSocket!.send(JSON.stringify({ type: 'ice-candidate', candidate, clientId: clientId || this.clientId }))
      this.emit('ice-candidate-sent', { candidate: candidate, clientId: clientId || this.clientId })
    }
  }

  public completeSession() {
    if (this.webSocket) {
      this.emit('completing-session', {})
      this.webSocket!.send(JSON.stringify({ type: 'completion' }))
    }
  }

  private handleAsHostMessage(message: { type: string; [key: string]: any }) {
    if (message.type === 'session-created') {
      this.emit('session-created', { sessionId: message.sessionId })
      this.internalSessionId = message.sessionId
    } else if (message.type === 'offer-request') {
      this.emit('offer-request', { clientId: message.clientId, metadata: message.metadata })
    } else if (message.type === 'answer') {
      this.emit('answer', { answer: message.answer, clientId: message.clientId })
    } else if (message.type === 'ice-candidate') {
      this.emit('ice-candidate', { candidate: message.candidate, clientId: message.clientId })
    }
  }

  private handleAsClientMessage(message: { type: string; [key: string]: any }, metadata?: Record<string, any>) {
    if (message.type === 'session-joined') {
      this.emit('session-joined', { sessionId: message.sessionId, clientId: message.clientId })
      this.internalSessionId = message.sessionId
      this.internalClientId = message.clientId
      this.webSocket!.send(JSON.stringify({ type: 'request-offer', sessionId: this.internalSessionId, clientId: this.internalClientId, metadata: metadata || {} }))
    } else if (message.type === 'offer') {
      this.emit('offer', { offer: message.offer, metadata: message.metadata })
    } else if (message.type === 'ice-candidate') {
      this.emit('ice-candidate', { candidate: message.candidate })
    } else if (message.type === 'promoted-to-host') {
      this.emit('promoted-to-host', { sessionId: message.sessionId })
      this.internalRole = SignalingRole.HOST
      this.webSocket!.onmessage = (event) => {
        this.handleMessage((message) => {
          this.handleAsHostMessage(message)
        }, event)
      }
    } else if (message.type === 'host-changed') {
      this.emit('host-changed', { sessionId: message.sessionId, newHostId: message.newHostId })
      this.webSocket!.send(JSON.stringify({ type: 'request-offer', sessionId: this.internalSessionId, clientId: this.internalClientId, metadata: metadata || {} }))
    }
  }

  private handleMessage(onMessage: (message: { type: string; [key: string]: any }) => void, event: MessageEvent) {
    try {
      const message = JSON.parse(event.data)

      onMessage(message)
    } catch (error) {
      this.emit('error-parsing-message', { error: error, message: event.data })
    }
  }
}
